import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { PaintBucket, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './PaintCoverageCalculatorPage/components/CalculationForm';
import ResultsOverview from './PaintCoverageCalculatorPage/components/ResultsOverview';
import PaintRecommendations from './PaintCoverageCalculatorPage/components/PaintRecommendations';
import QuickAdjustmentSidebar from './PaintCoverageCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './PaintCoverageCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './PaintCoverageCalculatorPage/components/TipsSidebar';
import InfoContent from './PaintCoverageCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  surfaceArea: '50', // Площадь поверхности в м²
  roomType: 'living', // Тип помещения
  surfaceType: 'drywall', // Тип поверхности
  paintType: 'water', // Тип краски
  paintQuality: 'medium', // Качество краски
  coatsNumber: '2', // Количество слоев
  wallsHeight: '2.7', // Высота стен в метрах
  ceilingIncluded: true, // Включен ли потолок
  surfaceCondition: 'normal', // Состояние поверхности
  calculationType: 'area', // Тип расчета ('area', 'room')
  roomLength: '5', // Длина комнаты (м)
  roomWidth: '4', // Ширина комнаты (м)
  windowsNumber: '2', // Количество окон
  windowHeight: '1.5', // Высота окна (м)
  windowWidth: '1.2', // Ширина окна (м)
  doorsNumber: '1', // Количество дверей
  doorHeight: '2.0', // Высота двери (м)
  doorWidth: '0.9', // Ширина двери (м)
  includeBaseboard: false, // Включать ли плинтус
  baseboardLength: '18', // Длина плинтуса (м)
  includeStucco: false, // Включать ли лепнину/молдинги
  stuccoLength: '0', // Длина лепнины (м)
  pricePerLiter: '350', // Цена краски за литр (руб.)
};

// Определение типов комнат
export const roomTypes = [
  { id: 'living', name: 'Жилая комната', description: 'Гостиная, спальня, детская' },
  { id: 'kitchen', name: 'Кухня', description: 'Помещение для приготовления пищи' },
  { id: 'bathroom', name: 'Ванная комната', description: 'Ванная, санузел' },
  { id: 'hallway', name: 'Коридор/Прихожая', description: 'Входная зона, коридор' },
  { id: 'children', name: 'Детская комната', description: 'Комната для детей' },
  { id: 'balcony', name: 'Балкон/Лоджия', description: 'Открытое или застекленное помещение' },
  { id: 'office', name: 'Домашний офис', description: 'Рабочий кабинет' },
];

// Типы поверхностей
export const surfaceTypes = [
  { id: 'drywall', name: 'Гипсокартон', absorption: 1.0 },
  { id: 'plaster', name: 'Штукатурка', absorption: 1.1 },
  { id: 'concrete', name: 'Бетон', absorption: 1.2 },
  { id: 'brick', name: 'Кирпич', absorption: 1.4 },
  { id: 'wood', name: 'Дерево', absorption: 1.3 },
  { id: 'metal', name: 'Металл', absorption: 0.8 },
  { id: 'wallpaper', name: 'Обои под покраску', absorption: 1.2 },
];

// Типы красок
export const paintTypes = [
  { id: 'water', name: 'Водоэмульсионная', coverage: 10, priceCategory: 'medium' },
  { id: 'acrylic', name: 'Акриловая', coverage: 12, priceCategory: 'high' },
  { id: 'latex', name: 'Латексная', coverage: 11, priceCategory: 'high' },
  { id: 'silicone', name: 'Силиконовая', coverage: 14, priceCategory: 'premium' },
  { id: 'alkyd', name: 'Алкидная', coverage: 11, priceCategory: 'high' },
  { id: 'oil', name: 'Масляная', coverage: 9, priceCategory: 'low' },
  { id: 'chalk', name: 'Меловая', coverage: 8, priceCategory: 'medium' },
  { id: 'mineral', name: 'Минеральная', coverage: 7, priceCategory: 'medium' },
  { id: 'silicate', name: 'Силикатная', coverage: 10, priceCategory: 'high' },
];

// Качество краски
export const paintQualities = [
  { id: 'economy', name: 'Эконом', coverageModifier: 0.8, priceRangeStart: 150, priceRangeEnd: 350 },
  { id: 'medium', name: 'Среднее', coverageModifier: 1.0, priceRangeStart: 350, priceRangeEnd: 650 },
  { id: 'premium', name: 'Премиум', coverageModifier: 1.2, priceRangeStart: 650, priceRangeEnd: 1200 },
  { id: 'luxury', name: 'Люкс', coverageModifier: 1.4, priceRangeStart: 1200, priceRangeEnd: 2500 },
];

// Состояния поверхности
export const surfaceConditions = [
  { id: 'new', name: 'Новая поверхность', absorptionModifier: 1.2 },
  { id: 'normal', name: 'Ранее окрашенная', absorptionModifier: 1.0 },
  { id: 'porous', name: 'Сильно пористая', absorptionModifier: 1.5 },
  { id: 'smooth', name: 'Гладкая', absorptionModifier: 0.9 },
];

/**
 * PaintCoverageCalculatorPage Component - Полный калькулятор расхода краски для различных поверхностей и типов помещений.
 */
const PaintCoverageCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('paint-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор расхода краски | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте точный объем краски для вашего ремонта: для стен, потолка, фасада, дерева и других поверхностей с учетом типа краски, количества слоев и особенностей помещения.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedPaintCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'walls':
          newState.calculationType = 'area';
          newState.ceilingIncluded = false;
          break;
        case 'ceiling':
          newState.calculationType = 'area';
          newState.ceilingIncluded = true;
          break;
        case 'room':
          newState.calculationType = 'room';
          break;
        case 'facade':
          newState.surfaceType = 'concrete';
          newState.paintType = 'silicone';
          break;
        case 'wood':
          newState.surfaceType = 'wood';
          newState.paintType = 'alkyd';
          break;
        case 'bathroom':
          newState.roomType = 'bathroom';
          newState.paintType = 'latex';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Расчет краски для ${formData.surfaceArea}м²`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedPaintCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedPaintCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <PaintBucket className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор расхода краски'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              roomTypes={roomTypes}
              surfaceTypes={surfaceTypes}
              paintTypes={paintTypes}
              paintQualities={paintQualities}
              surfaceConditions={surfaceConditions}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <PaintRecommendations 
                  results={results}
                  roomType={formData.roomType}
                  surfaceType={formData.surfaceType}
                  paintType={formData.paintType}
                />
              </>
            )}

            {/* --- Информационный контент о красках --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default PaintCoverageCalculatorPage;